import React from "react";
import {
  iconDefinitions,
  IconName,
  IconDefinition,
} from "./iconDefinitions";
import { cssVars as palette } from "../../theme/cssVars";

// ─── Types ────────────────────────────────────────────────────────────────────

/** Dimension value: number (px) or CSS string (e.g. "1.5rem"). */
type IconDimension = number | string;

export interface SvgIconProps
  extends Omit<React.SVGProps<SVGSVGElement>, "color"> {
  /** Icon name — must match a key in `iconDefinitions`. */
  name: IconName;

  /**
   * Shorthand for setting both width and height to the same value.
   * Overridden by explicit `width` or `height` props.
   */
  size?: IconDimension;

  /**
   * Sets the CSS `font-size` on the SVG element.
   * Because the default width/height is `1em`, this effectively scales the
   * icon when neither `size` nor explicit `width`/`height` are given.
   */
  fontSize?: IconDimension;

  /**
   * Sets the CSS `color`. Paths use `fill="currentColor"` so they inherit
   * this value. If omitted, the icon inherits the surrounding text color.
   *
   * Accepts CSS color values (`"red"`, `"#d32f2f"`) or semantic token names
   * matching the project's Tailwind config (`"primary"`, `"secondary"`,
   * `"error"`, `"warning"`, `"info"`, `"success"`). The special value
   * `"inherit"` is treated as no override (the icon inherits its parent color).
   *
   * Precedence: explicit `style.color` > `color` prop > inherited.
   */
  color?: string;

  /**
   * Accessible title rendered as `<title>` inside the SVG.
   * When provided (or when `aria-label` is set), the icon becomes a
   * meaningful image (`role="img"`). Otherwise it's decorative
   * (`aria-hidden="true"`, `focusable="false"`).
   */
  title?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Lightweight SVG icon component that renders icon geometry from the local
 * `iconDefinitions` registry. Zero external dependencies.
 *
 * ## Dimension behavior
 *
 * Priority (highest first):
 * 1. Explicit `width` / `height` props
 * 2. `size` prop (sets both width and height)
 * 3. Falls back to `"1em"` — scales with the element's font-size
 *
 * `fontSize` sets the CSS `font-size` on the SVG. Combined with the `1em`
 * default, this controls rendered size when `size`/`width`/`height` are absent.
 *
 * ## Color behavior
 *
 * All paths use `fill="currentColor"`. The `color` prop sets CSS `color` on
 * the SVG so paths inherit it. An explicit `style.color` overrides the prop.
 *
 * ## Accessibility
 *
 * - Decorative (no `title`/`aria-label`): `aria-hidden="true"`, `focusable="false"`
 * - Meaningful (`title` or `aria-label` provided): `role="img"`
 *
 * Icons inside labeled interactive elements (e.g. `<button aria-label="...">`)
 * should remain decorative — the parent already provides the accessible name.
 *
 * ## Styling precedence
 *
 * 1. Internal defaults (width/height `1em`, `fill="currentColor"`)
 * 2. Convenience props (`fontSize`, `color`, `size`)
 * 3. Explicit `style` object (caller wins)
 *
 * This means `style={{ color: "blue" }}` overrides `color="red"`.
 */

/**
 * Semantic color token map — mirrors the project's tailwind.config.js palette.
 * "inherit" is intentionally mapped to `undefined` so no inline color is set
 * and the icon inherits its parent's CSS color.
 */
const SEMANTIC_COLORS: Record<string, string | undefined> = {
  primary: palette.primary,
  secondary: palette.secondary,
  error: palette.error,
  warning: palette.warning,
  info: palette.info,
  success: palette.success,
  inherit: undefined,
};

/**
 * MUI fontSize token map — matches the pixel sizes MUI uses for its
 * `fontSize` prop so existing call sites (`fontSize="small"` etc.) keep
 * their intended dimensions after migration.
 */
const FONT_SIZE_TOKENS: Record<string, string> = {
  inherit: "inherit",
  small: "1.25rem",   // MUI 20px
  medium: "1.5rem",   // MUI 24px (default)
  large: "2.1875rem", // MUI 35px
};

export const SvgIcon = React.forwardRef<SVGSVGElement, SvgIconProps>(
  function SvgIcon(
    {
      name,
      size,
      fontSize,
      color,
      title,
      width,
      height,
      style,
      className,
      children,
      "aria-label": ariaLabel,
      "aria-hidden": ariaHiddenProp,
      role: roleProp,
      ...rest
    },
    ref
  ) {
    const def = iconDefinitions[name] as unknown as IconDefinition;

    // ── Dimensions ──────────────────────────────────────────────────────────
    const resolvedWidth = width ?? (size != null ? size : "1em");
    const resolvedHeight = height ?? (size != null ? size : "1em");

    // ── Color resolution ────────────────────────────────────────────────────
    // Map semantic token names (matching tailwind.config.js) to actual colors.
    // "inherit" means "use parent's color" — we simply don't set a color.
    const resolvedColor = color != null ? SEMANTIC_COLORS[color] ?? color : undefined;

    // ── Font-size resolution ────────────────────────────────────────────────
    // Map MUI fontSize tokens ("small", "medium", "large", "inherit") to CSS
    // values so migrated call sites keep their intended dimensions.
    const resolvedFontSize =
      fontSize != null
        ? (typeof fontSize === "string" && fontSize in FONT_SIZE_TOKENS
            ? FONT_SIZE_TOKENS[fontSize]
            : fontSize)
        : undefined;

    // ── Style merge ─────────────────────────────────────────────────────────
    const mergedStyle: React.CSSProperties = {
      // Defaults (layer 1)
      ...(resolvedFontSize != null && { fontSize: resolvedFontSize }),
      ...(resolvedColor != null && { color: resolvedColor }),
      // Caller overrides (layer 3) — wins over convenience props
      ...style,
    };

    // ── Accessibility ───────────────────────────────────────────────────────
    const isMeaningful = Boolean(title || ariaLabel);
    const ariaHidden = ariaHiddenProp ?? (isMeaningful ? undefined : true);
    const role = roleProp ?? (isMeaningful ? "img" : undefined);

    return (
      <svg
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        viewBox={def.viewBox}
        width={resolvedWidth}
        height={resolvedHeight}
        fill="currentColor"
        className={className}
        style={mergedStyle}
        aria-hidden={ariaHidden}
        aria-label={ariaLabel}
        role={role}
        focusable={isMeaningful ? undefined : "false"}
        {...rest}
      >
        {title && <title>{title}</title>}

        {def.paths.map((path, i) => (
          <path
            key={`p${i}`}
            d={path.d}
            opacity={path.opacity}
            fill={path.fill}
            fillRule={path.fillRule}
            clipRule={path.clipRule}
          />
        ))}

        {def.circles?.map((circle, i) => (
          <circle
            key={`c${i}`}
            cx={circle.cx}
            cy={circle.cy}
            r={circle.r}
            opacity={circle.opacity}
            fill={circle.fill}
          />
        ))}

        {def.rects?.map((rect, i) => (
          <rect
            key={`r${i}`}
            x={rect.x}
            y={rect.y}
            width={rect.width}
            height={rect.height}
            rx={rect.rx}
            opacity={rect.opacity}
            fill={rect.fill}
          />
        ))}

        {children}
      </svg>
    );
  }
);

SvgIcon.displayName = "SvgIcon";
